import * as Sentry from '@sentry/react';
import classNames from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import { observer } from 'mobx-react-lite';
import type { FlowType } from 'paypal-checkout-components';
import { useCallback, useEffect, useState } from 'react';

import LoadingIndicator from '@components/common/loading-indicator/loading-indicator';
import { useStores } from '@components/common/root-store-provider/root-store-provider';
import { useAnalytics } from '@features/Analytics';
import {
  AnalyticsPaymentMethod,
  AnalyticsPaymentSystem,
  PaymentCompletionStatus,
  braintreeClientTokenAtom,
  createBraintreeSubscription,
  handleCheckoutOpenRequest,
  usePaymentCompletionStatus,
} from '@features/Payments';
import {
  paymentMethodWithPersistence,
  selectedPlanAtom,
  userPaymentsInfoAtom,
} from '@features/Stripe/atoms';
import type { PaypalButtonBraintreeProps } from '@features/Stripe/widgets/checkout-form/widgets/paypal-button-braintree/types';
import { PaymentMethod } from 'src/models/payments-store/payments-store';
import { useGetAppName } from 'src/utils/hooks';
import { initializePaypalSDK } from '../../utils/paypalSingleton';

import PaypalLogo from 'src/assets/images/payment/paypal-logo.svg';

import { useFeature } from '@features/FeatureToggle';
import styles from './paypal-button-braintree.module.scss';

const PAYPAL_BUTTONS_CONTAINER_ID = 'paypal-buttons-container';

export const PaypalButtonBraintree = observer(
  ({
    className,
    subscriptionPlan,
    buttonContent = <PaypalLogo />,
    isCustomButton = false,
    id = PAYPAL_BUTTONS_CONTAINER_ID,
    handleAddPaymentInfoEventsSend,
    onLoadEnd,
    onSuccess,
    cancelSubscriptionAtPeriodEnd,
    ...props
  }: PaypalButtonBraintreeProps) => {
    const fbPixelSept2025Feature = useFeature('fbPixelSept2025Feature');
    const { authStore, paymentsStore } = useStores();
    const { user, auth_token, variant } = authStore;
    const app = useGetAppName();
    const braintreeClientToken = useAtomValue(braintreeClientTokenAtom);
    const { introOfferData } = useAtomValue(selectedPlanAtom);
    const { trackFacebookEvent, trackGoogleEvent, trackPinterestEvent } =
      useAnalytics();
    const { setPaymentCompletionStatus } = usePaymentCompletionStatus();

    const setPaymentMethod = useSetAtom(paymentMethodWithPersistence);
    const setPaymentInfo = useSetAtom(userPaymentsInfoAtom);

    const [isLoading, setIsLoading] = useState(true);
    const [buttonId] = useState(
      () => `paypal-button-${Math.random().toString(36).slice(2, 9)}`,
    );

    const braintreeHandle = useCallback(async () => {
      setIsLoading(true);

      if (!user) {
        throw new Error('A user is required');
      }

      if (!braintreeClientToken) {
        throw new Error('Client token is required');
      }

      try {
        const paypalCheckout = await initializePaypalSDK(braintreeClientToken);

        const buttons = window.paypal.Buttons({
          style: { height: 48 },
          // @ts-ignore (an issue with library typings)
          fundingSource: window.paypal.FUNDING.PAYPAL,
          createBillingAgreement: () => {
            paymentsStore.setPaymentMethod(PaymentMethod.PAYPAL);
            handleCheckoutOpenRequest({
              subscriptionPlan: {
                id: subscriptionPlan.id,
                fullPriceDiscount: subscriptionPlan.fullPriceDiscount,
              },
              paymentMethod: AnalyticsPaymentMethod.PAYPAL,
              paymentSystem: AnalyticsPaymentSystem.BRAINTREE,
              email: user!.email,
              variant: variant,
            });
            !fbPixelSept2025Feature &&
              trackFacebookEvent({
                eventName: 'AddPaymentInfo',
                customData: {
                  paymentMethod: 'payPal',
                  paymentSystem: 'braintree',
                },
                options: {
                  email: user?.email,
                },
              });
            trackPinterestEvent({ eventName: 'AddToCart' });
            return paypalCheckout.createPayment({
              flow: 'vault' as (typeof FlowType)['Vault'],
            });
          },
          onCancel: () => {
            setIsLoading(false);
            trackGoogleEvent({
              eventName: 'add_payment_info_skipped',
              options: {
                payment_system: 'braintree',
                payment_method: AnalyticsPaymentMethod.PAYPAL,
              },
            });
          },
          onApprove: async (data) => {
            setIsLoading(true);
            try {
              const authorizationResponse =
                await paypalCheckout.tokenizePayment(data);
              const { bt_subscription_id, customer_id } =
                await createBraintreeSubscription({
                  customerId: user!.serverId.toString(),
                  customerToken: auth_token!,
                  merchantAccountId: subscriptionPlan.merchantAccountId,
                  planId: subscriptionPlan.braintreePlanId!,
                  paymentMethodNonce: authorizationResponse.nonce,
                  isStripePAth: true,
                  btIntroOfferAmount: `${introOfferData?.fullPriceDiscount}`,
                  btIntroOfferCurrency: introOfferData?.currency,
                  cancelAtPeriodEnd: cancelSubscriptionAtPeriodEnd,
                  app,
                });
              setPaymentMethod('paypal');
              setPaymentInfo((prev) => ({
                ...prev,
                paymentMethod: 'paypal',
                paymentSystem: 'braintree',
                btSubscriptionId: bt_subscription_id,
                btCustomerId: customer_id,
              }));
              await onSuccess({
                paymentMethod: 'paypal',
                paymentSystem: 'braintree',
                btSubscriptionId: bt_subscription_id,
                btCustomerId: customer_id,
              });
              return authorizationResponse;
            } finally {
              setIsLoading(false);
            }
          },
          onClick: () => {
            setIsLoading(true);
            handleAddPaymentInfoEventsSend({
              paymentSystem: 'braintree',
              paymentMethod: 'paypal',
            });
          },
          onError: (error) => {
            setIsLoading(false);
            const errorPhrases = [
              /Error: Detected popup close/,
              /Error: zoid destroyed all components/,
              /Error: Window is closed/,
            ];

            if (errorPhrases.some((phrase) => phrase.test(error))) {
              console.log('detected popup close');
              return;
            }
            Sentry.captureException(error);
            trackGoogleEvent({
              eventName: 'payment_error',
              options: {
                errorCode: error,
                errorDeclineCode: '',
                paymentMethod: 'paypal',
                paymentSystem: 'Braintree',
              },
            });

            setPaymentCompletionStatus({
              value: PaymentCompletionStatus.FAILURE,
              selectedPlanId: subscriptionPlan.id,
            });
          },
        });

        const container = document.getElementById(buttonId);

        if (paypalCheckout && container && !container.hasChildNodes()) {
          buttons.render(`#${buttonId}`);
        }

        setIsLoading(false);
        onLoadEnd();
      } catch (error) {
        console.error('Error initializing PayPal:', error);
        setIsLoading(false);
        onLoadEnd();
      }
    }, [
      fbPixelSept2025Feature,
      buttonId,
      app,
      user,
      braintreeClientToken,
      onLoadEnd,
      paymentsStore,
      setPaymentInfo,
      subscriptionPlan.id,
      subscriptionPlan.fullPriceDiscount,
      subscriptionPlan.merchantAccountId,
      subscriptionPlan.braintreePlanId,
      variant,
      trackFacebookEvent,
      trackPinterestEvent,
      trackGoogleEvent,
      auth_token,
      introOfferData?.fullPriceDiscount,
      introOfferData?.currency,
      setPaymentMethod,
      onSuccess,
      handleAddPaymentInfoEventsSend,
      setPaymentCompletionStatus,
      cancelSubscriptionAtPeriodEnd,
    ]);

    useEffect(() => {
      if (braintreeClientToken !== '') {
        braintreeHandle().then((r) => r);
      }
    }, [braintreeClientToken, braintreeHandle]);

    return (
      <div className={classNames(styles.container, className)} {...props}>
        {isLoading ? (
          <div className={styles.loaderContainer}>
            <LoadingIndicator />
          </div>
        ) : (
          buttonContent
        )}
        <div
          data-testid="bt-paypal-button"
          className={styles.frame}
          style={{ opacity: isCustomButton ? 0.0001 : 1 }}
          id={buttonId}
        />
      </div>
    );
  },
);
